/**
 * Reading an asset entry well enough to draw one row.
 *
 * An asset entry is `{type, bucket, source, sourceRef}` and its `source` is one
 * of three things: inline bytes (`data:…`), an inline pair (`{image, xml|json}`),
 * or a reference to a stored media record (`@server:<id>`) that only the
 * preview and the Assets tab can resolve. These helpers answer the two questions
 * a row asks — "is there something an `<img>` can show?" and "how big is it?" —
 * and answer them the same way the Assets tab does.
 *
 * PURE. No React, no fetching, no config writes; the panel owns those.
 */

/** A stored-media reference, which is a promise of bytes rather than bytes. */
export const SERVER_REF_PREFIX = "@server:";

/** True for a `source` that names a media record instead of carrying data. */
export function isServerRef(source) {
  if (typeof source === "string") return source.startsWith(SERVER_REF_PREFIX);
  if (source && typeof source === "object") return isServerRef(source.image);
  return false;
}

/**
 * The bytes an `<img>` can show, or null.
 *
 * A `data:image/…` URL only: handing an `<img>` a `@server:` reference or a font
 * would paint a broken-image icon, which reads as "this asset is damaged" when
 * the truth is "this row has nothing to preview".
 */
export function thumbnailFromEntry(entry) {
  const source = entry && entry.source;
  if (typeof source === "string" && source.startsWith("data:image/")) return source;
  // A pair (atlas, bitmap font) previews as its image half; the metrics half is
  // XML or JSON and has nothing to show.
  if (source && typeof source === "object" && typeof source.image === "string") {
    return source.image.startsWith("data:image/") ? source.image : null;
  }
  return null;
}

/**
 * Roughly how much of the snapshot this asset is.
 *
 * base64 carries three bytes per four characters — close enough to label a row,
 * and the same approximation the Assets tab prints, so the two agree.
 */
export function describeAssetSize(entry) {
  const source = entry && entry.source;
  if (isServerRef(source)) return "stored";

  const text =
    typeof source === "string"
      ? source
      : source && typeof source === "object"
        ? `${source.image || ""}${source.xml || source.json || ""}`
        : "";
  if (!text) return "no source";

  const bytes = Math.round((text.length * 3) / 4);
  const megabytes = bytes / (1024 * 1024);
  return megabytes >= 1
    ? `${megabytes.toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}
